import { createContext, FC, MutableRefObject, PropsWithChildren, RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ManagerOptions, Socket, SocketOptions } from "socket.io-client";
import { useWebsocket } from "../hooks/use-websocket";
import { useAppDispatch, useAppSelector } from "../../common/store";
import { appStateSelector } from "../../common/store/app-state/selectors";
import { setHubSpotMeetingScheduled, setIsLANudge, setIsNudgeOpen, setIsOpen } from "../../common/store/app-state/app-state-slice";
import { getChatMessages } from "../../common/store/chat/thunks/get-chat-messages";
import { GetMessage } from "./types/get-message";
import { SocketMessageType } from "./constants/socket-message-type";
import { v4 } from "uuid";
import { readPageVisibleText } from "../utils/read-page-visible-text";
import { useDeviceDetector } from "../hooks/use-device-detector";
import { useSearchParams } from "react-router-dom";
import { addMessage, handleNewResponse, setGetFeedback, setIsDoneAnimationBuffer, setIsStreamingHandling, setSuggestionChips, setIsChattingWithLiveAgent, setStreamingMessageTurnId, setInputCollection, setIsWaitingLiveAgent, stopWaitingLA, setMeetingDetails, setAgentDetails, setAvailableSlots, setIsPingedByLA, setReqNudgeConfig } from "../../common/store/chat/chat-slice";
import { NewResponse } from "../../common/store/chat/types/new-response";
import { handleNewResponseComplete } from "../../common/store/chat/thunks/handleNewResponseComplete";
import { chatSelector } from "../../common/store/chat/selectors";
import { ChatMessage } from "../../common/store/chat/types/chat-message";
import { sendMessageLogSubmission } from "../../common/store/chat/thunks/send-message-log";
import VurbalizeEvents from "../../common/types/dto/chat/vurbalize-events";
import getMessageByVurablizeEvent from "../utils/get-message-by-vurablize-event";
import { IntentType } from "../../common/store/chat/contants/intent-type";
import { shouldDisplay } from "../../common/store/app-state/thunks/should-display";
import getHiddenData from "../utils/get-hidden-data";
import { LiveAgentResponse } from "../../common/store/chat/types/live-agent-response";
import { setBookAppointment } from "../../common/store/theme/theme-slice";
import { BookAppointmentImplementation } from "../../common/store/theme/constants/book-appointment-implementation";
import scrollToLastMessage from "../utils/scroll-to-last-message";
import IVurbalizeSendMessage from "./types/vurbalize-send-message";
import useAudio from "../hooks/use-audio";
import getCommonData from "../utils/get-common-data";
import { getQueryParamValue } from "../utils/get-query-param-value";
import { endCall, setAgentName, setAgentPhotoURL } from "../../common/store/calls/calls-slice";
import { RootState } from "../../common/store/types";
import { CallbackData } from "../../common/store/theme/types/CallbackData";
import getBasicLSData from "../utils/getBasicLSData";
import { OfferCalendarResponse } from "../../common/store/chat/types/offer-calendar-response";
import { fetchMeetingDetails } from "../utils/fetch-meeting-details";

import useTypingAnimation from "../hooks/use-type-animation";
import { PostShouldDisplayResponseDto } from "../../common/types/dto/app/post-should-display-response-dto";
export interface MessageToServerPayload {
  message?: string;
  newChat?: boolean;
  isNudge?: boolean;
  searchTerm?: string;
  type?: IVurbalizeSendMessage['type'];
  appState?: RootState["app-state"];
  isPingedByLA?: boolean;
}

interface IChatContext {
  socket: MutableRefObject<Socket | undefined> | null;
  sendUserInput: ((message: string) => void) | null;
  sendSuggestionChipInput: ((message: string) => void) | null;
  messagesContainerRef: RefObject<HTMLDivElement> | null;
  offsetRef: RefObject<HTMLDivElement> | null;
  lastMessage: {typeA: ChatMessage, typeU: ChatMessage, typeAgent: ChatMessage} | null;
  agentTurnId: string;
  sendMessageToServer: ((data: MessageToServerPayload) => void) | null,
  handleStopWaitingLA: (() => void) | null,
  userTyping: () => void;
  forceStopUserTyping: () => void;
  markAllMessagesAsRead: () => void;
  isAgentTyping: boolean;
  setIsAgentTyping: (isTyping: boolean) => void;
}

const chatContextInitialValues: IChatContext = {
  socket: null,
  sendUserInput: null,
  sendSuggestionChipInput: null,
  messagesContainerRef: null,
  offsetRef: null,
  lastMessage: null,
  agentTurnId: '',
  sendMessageToServer: null,
  handleStopWaitingLA: null,
  userTyping: () => {},
  forceStopUserTyping: () => {},
  markAllMessagesAsRead: () => {},
  isAgentTyping: false,
  setIsAgentTyping: () => {},
};

export const ChatContext = createContext<IChatContext>(chatContextInitialValues);

export const ChatContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const storeAppState = useAppSelector(appStateSelector);
  const storeChat = useAppSelector(chatSelector);
  const dispatch = useAppDispatch();
  const device = useDeviceDetector();
  const [searchParams] = useSearchParams();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef<HTMLDivElement>(null);
  const [timeStart, setTimeStart] = useState(new Date().getTime());
  const [agentTurnId, setAgentTurnId] = useState('');
  const [isFirstResponse, setIsFirstResponse] = useState(true);
  const { playAudio } = useAudio();
  const [welcomeMessage, setWelcomeMessage] = useState({ isSent: false, isReceived: false });
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const searchParamsMap = useMemo(() => {
    const out: Record<string, Array<string>> = {};

    for (const key of searchParams.keys()) {
      if (Object.prototype.hasOwnProperty.call(out, key)) {
        continue;
      }
      
      out[key] = searchParams.getAll(key);
    }

    return out;
  }, [searchParams]);

  // Ref to always hold the latest agent message content
  const lastAgentMessageRef = useRef<string>('');
  // Ref to accumulate the full agent message during streaming
  const agentMessageAccumulatorRef = useRef<string>('');

  const [uri, options] = useMemo<[string, Partial<ManagerOptions & SocketOptions>]>(() => [
    process.env.API_URL as string,
    {
      transports: ['websocket'],
      auth: {
        session_room: storeAppState.suId,
        conversation_id: storeAppState.conversationId,
        su_id: storeAppState.suId,
        session_id: storeAppState.sessionId,
        merch_id: storeAppState.merchId,
      },
    }
  ], [storeAppState.suId]);

  const socket = useWebsocket(uri, options);

  useEffect(() => {
    const onSendMessage = (data: IVurbalizeSendMessage) => {
      const { isHiddenInChat, message, type } = data;

      if (!isHiddenInChat) {
        dispatch(setIsOpen(true));
      }

      if (!message) return;

      if (isHiddenInChat) {
        sendMessageToServer({ message, type });
      } else {
        sendUserInput(message, type);
      }
    }

    const prepareDataForDemo = () => {
      const data = getCommonData(storeAppState);
      vurbalize.emit(VurbalizeEvents.DEMO_DATA_RECEIVE, data);
    }

    vurbalize.on(VurbalizeEvents.VURBALIZE_USER_SEND_MESSAGE, onSendMessage)
    vurbalize.on(VurbalizeEvents.GET_DATA_FOR_DEMO, prepareDataForDemo);
    //Open Callback form from the last step of demo
      vurbalize.on('OPEN_CALLBACK_FORM', (data : CallbackData) => {
        dispatch(
          setBookAppointment({
            implementation: BookAppointmentImplementation.CALLBACK
          })
        );
      });
      vurbalize.on('OPEN_CALENDLY', (data : CallbackData) => {
        dispatch(
          setBookAppointment({
            implementation: BookAppointmentImplementation.CALENDLY
          })
        );
      });
    return () => {
      vurbalize.off(VurbalizeEvents.VURBALIZE_USER_SEND_MESSAGE, onSendMessage);
      vurbalize.off(VurbalizeEvents.GET_DATA_FOR_DEMO, prepareDataForDemo);
      vurbalize.off('OPEN_CALLBACK_FORM');
      vurbalize.off('OPEN_CALENDLY');
    }
  }, [])

  const sendInitialRequest = useCallback((newChat = false) => {
    scrollContainerToBottom();
    if (storeChat.isChattingWithLiveAgent) return;

    const msgObj: {newChat: boolean, isNudge: boolean, searchTerm?: string} = {newChat, isNudge: true};

    const searchStr = getQueryParamValue('utm_term');

    setWelcomeMessage({ ...welcomeMessage, isSent: true });

    const data = sendMessageToServer({...msgObj, searchTerm: newChat ? '' : searchStr || ''});
    if (!data) return;

    // dispatch(setIsStreamingHandling(true));
    
  }, [storeAppState, readPageVisibleText, searchParamsMap, storeChat.isChattingWithLiveAgent]);

  const sendMessageToServer = (
    { message, newChat = false, searchTerm, isNudge = false, type, appState, isPingedByLA }: MessageToServerPayload
  ) => {
    setOffset(0);
    const { current } = socket;

    if (!current) {
      return;
    }
    const {
      botVersion,
      botType,
      visitorCountryCodeSource,
      visitorCountryCodeKey,
      visitorCountryKey,
      visitorCountrySource,
    } = storeAppState;

    const agent_turn_id = v4();
    const commonData = getCommonData(appState || storeAppState);

    const basicLSData = getBasicLSData(visitorCountryCodeSource, visitorCountryCodeKey, visitorCountrySource, visitorCountryKey);
    const data: GetMessage = {
      ...commonData,
      new_chat_flag: newChat,
      turn_id: v4(),
      agent_turn_id,
      merch_id: commonData.merchant_id,
      bot_version: botVersion,
      current_url: window.location.href,
      urlParams: searchParamsMap,
      device_input: device.device?.type,
      bot_type: botType,
      current_context: readPageVisibleText(),
      user_input: message,
      hiddenData: getHiddenData(),
      is_nudge: isNudge,
      type,
      is_pinged_user: isPingedByLA,

      // Will be used by live agent routing algorithm
      ...basicLSData,
      // icp: basicLSData.ICP,
    };

    if (message && !isNudge) {
      data.user_input = message;
    }

    if (typeof searchTerm === 'string') {
      data.search_term = searchTerm
    }

    current.emit(SocketMessageType.GET_RESPONSE, data);
    setAgentTurnId(data.agent_turn_id);
    dispatch(setSuggestionChips([]));
    dispatch(setIsPingedByLA(false));
    return data;
  }

  const userTypingStartTimerRef = useRef<NodeJS.Timeout | null>(null);
  const userTypingStopTimerRef  = useRef<NodeJS.Timeout | null>(null);
  
  // State for user typing
  const [isUserTypingState, _setIsUserTypingState] = useState(false);
  const isUserTypingRef = useRef(isUserTypingState);

  // Wrapper to set state and ref together
  const setIsUserTyping = useCallback((typing: boolean) => {
    isUserTypingRef.current = typing;
    _setIsUserTypingState(typing);
  }, []); // This callback itself has no dependencies as it only defines how to set state/ref
  const emitUserTyping = useCallback((typing: boolean) => {
    const {current} = socket;
    if (!current) return;
    if (storeChat.isChattingWithLiveAgent){
       current.emit("user_typing", {
        su_id: storeAppState.suId,
        is_typing: typing,
      });
    }
  }, [socket, storeAppState.suId,storeChat.isChattingWithLiveAgent]); // Depends on socket and suId

  const markAllMessagesAsRead = useCallback(() => {
    const { current } = socket;
    if (!current) return;
  
    current.emit(SocketMessageType.MARK_ALL_MESSAGES_AS_READ, {
      merchant_id: storeAppState.merchId,
      su_id: storeAppState.suId,
      conversation_id: storeAppState.conversationId,
      session_id: storeAppState.sessionId
    });
  }, [socket, storeAppState.isOpen, storeAppState.sessionId]);  


  const userTyping = useCallback(() => {
    // Clear the stop timer whenever there is typing activity
    if (userTypingStopTimerRef.current) {
      clearTimeout(userTypingStopTimerRef.current);
      userTypingStopTimerRef.current = null;
    }

    // Set a timer to stop typing after inactivity
    userTypingStopTimerRef.current = setTimeout(() => {
      if (isUserTypingRef.current) { // Read from ref for the most current state
        setIsUserTyping(false);      // Use wrapper
        emitUserTyping(false);
      }
      userTypingStopTimerRef.current = null;
    }, 2000); // Stop typing after 2000ms of inactivity

    // If not currently typing (according to ref), set a timer to start typing after a delay
    if (!isUserTypingRef.current && !userTypingStartTimerRef.current) {
      userTypingStartTimerRef.current = setTimeout(() => {
        setIsUserTyping(true); // Use wrapper
        emitUserTyping(true);
        userTypingStartTimerRef.current = null;
      }, 500); // Start typing after 500ms of continuous activity
    }
  }, [setIsUserTyping, emitUserTyping]); // Depends on the stable setIsUserTyping and emitUserTyping

  const forceStopUserTyping = useCallback(() => {
    if (userTypingStartTimerRef.current) {
      clearTimeout(userTypingStartTimerRef.current);
      userTypingStartTimerRef.current = null;
    }
    if (userTypingStopTimerRef.current) {
        clearTimeout(userTypingStopTimerRef.current);
        userTypingStopTimerRef.current = null;
    }
    if (isUserTypingRef.current) { // Read from ref
      setIsUserTyping(false);    // Use wrapper
      emitUserTyping(false);
    }
  }, [setIsUserTyping, emitUserTyping]); // Depends on the stable setIsUserTyping and emitUserTyping


  // and don’t forget cleanup on unmount
  useEffect(() => {
    return () => {
      if (userTypingStartTimerRef.current) clearTimeout(userTypingStartTimerRef.current);
      if (userTypingStopTimerRef.current)  clearTimeout(userTypingStopTimerRef.current);
    };
  }, []);


  const [isReInitChat, setIsReInitChat] = useState(false);
  const toggleInit = (value?: boolean) => {
    setIsReInitChat((prevValue) => value || !prevValue)
  };

  // get chat history && make initial call if new user
  useEffect(() => {
    if (!storeAppState.isReadyHeartbeat) return
    (async () => {
      try {
        const shouldDisplayResult = await dispatch(shouldDisplay());
        const res = shouldDisplayResult.payload as PostShouldDisplayResponseDto;
        dispatch(setReqNudgeConfig(res.nudge_config))
        const payloadAction = await dispatch(getChatMessages());
        const payload = payloadAction.payload as { messages: ChatMessage[] };
        const messages = payload.messages || [];
        sendInitialRequest(!messages.length);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [storeAppState.isReadyHeartbeat,storeChat.reqNudgeType]);

  useEffect(() => {
    if (isReInitChat && storeChat.isDoneAnimationBuffer) {
      dispatch(getChatMessages());
      toggleInit(false);
    };
  }, [isReInitChat, storeChat.isDoneAnimationBuffer])

  const sendMessageLog = ({message, turn_id, suggestion_clicked = 0}: {message: string, turn_id: string, suggestion_clicked?: number}) => {
    if (storeChat.messages?.length === 1) {
      dispatch(sendMessageLogSubmission({
        data: {
          message: getMessageByVurablizeEvent(VurbalizeEvents.USERS_FIRST_MESSAGE),
          event_name: VurbalizeEvents.USERS_FIRST_MESSAGE,
          event_data: {
            timestamp: Date.now(),
            messageContent: message,
          },
        }
      }))
    }

    dispatch(sendMessageLogSubmission({
      data: {
        message,
        event_name: VurbalizeEvents.USER_SEND_MESSAGE,
        event_data: {
          timestamp: Date.now(),
          messageContent: message,
        },
        turn_id,
        user_turn_id: turn_id,
        suggestion_clicked,
      }
    }))

    if (suggestion_clicked) {
      dispatch(sendMessageLogSubmission({
        data: {
          message: getMessageByVurablizeEvent(VurbalizeEvents.SUGGESTION_TEXT_CLICKED),
          content: message,
          event_name: VurbalizeEvents.SUGGESTION_TEXT_CLICKED,
          event_data: {
            timestamp: Date.now(),
            suggestionText: message,
          },
          turn_id,
        }
      }));
    }
  }

  useEffect(() => {
    dispatch(setStreamingMessageTurnId(agentTurnId))
  }, [agentTurnId])

  useEffect(() => {
    if (!storeAppState.isChatWebsocketConnected || !storeAppState.isReadyHeartbeat) return;
    let currentPath = window.location.pathname;
    const interval = setInterval(() => {
      if(window.location.pathname !== currentPath) {
        // dispatch(setIsNudgeOpen(true));
        currentPath = window.location.pathname;
        sendInitialRequest();
      }
    }, 50);

    return () => {
      clearInterval(interval);
    }
  }, [storeAppState.isChatWebsocketConnected, storeAppState.isReadyHeartbeat])
  
  const sendUserInput = useCallback((message: string, type?: IVurbalizeSendMessage['type']) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;


    const data = sendMessageToServer({ message, type, isPingedByLA: storeChat.isPingedByLA });
    if (!data) return;

    dispatch(addMessage({
      itemType: "U",
      value: message,
      turn_id: data.turn_id,
      conversation_id: storeAppState.conversationId,
    }));
    sendMessageLog({ message, turn_id: data.turn_id });
    if (storeChat.isChattingWithLiveAgent) return;

    dispatch(setIsStreamingHandling(true));
    dispatch(setIsDoneAnimationBuffer(false));
    dispatch(setInputCollection(null));
    setTimeStart(new Date().getTime());
  }, [storeAppState, readPageVisibleText, searchParamsMap, storeChat.isChattingWithLiveAgent, storeChat.isPingedByLA ]);

  const sendSuggestionChipInput = useCallback((message: string) => {
    // Force stop typing when sending a suggestion chip
    forceStopUserTyping();

    const data = sendMessageToServer({ message });
    if (!data) return;

    dispatch(addMessage({
      itemType: "U",
      value: message,
      turn_id: data.turn_id,
      conversation_id: storeAppState.conversationId,
    }));
    dispatch(setIsStreamingHandling(true));
    dispatch(setIsDoneAnimationBuffer(false));
    dispatch(setInputCollection(null));
    sendMessageLog({ message, turn_id: data.turn_id, suggestion_clicked: 1 });
    setTimeStart(new Date().getTime());
  }, [storeAppState, readPageVisibleText, searchParamsMap, forceStopUserTyping]);



  const handleServerNewResponse = useCallback((e: NewResponse) => {
    dispatch(handleNewResponse({ response: e, lastAgentTurnId: agentTurnId }));
    if (!storeChat.isChattingWithLiveAgent) {
      dispatch(setStreamingMessageTurnId(agentTurnId))
    };
    const timeEnd = new Date().getTime();
    const delta = timeEnd - timeStart;

    // Track the latest agent message content
    if (e.system_text_response) {
      lastAgentMessageRef.current = e.system_text_response;
      // Accumulate the full agent message during streaming
      agentMessageAccumulatorRef.current += e.system_text_response;
    }

    if (e.intent === IntentType.QNA && isFirstResponse) {
      reCalculateOffset(true);
      scrollToLastMessage({ block: 'start' });
      setIsFirstResponse(false);
      dispatch(sendMessageLogSubmission({
        data: {
          message: getMessageByVurablizeEvent(VurbalizeEvents.DELTA_LOGS_MESSAGE_API),
          event_name: VurbalizeEvents.DELTA_LOGS_MESSAGE_API,
          turn_id: agentTurnId,
          diff_in_mili_seconds: delta,
          diff: delta / 1000,
        }
      }));
    }
    if (e.intent === IntentType.CALENDLY_SCHEDULE) {
      dispatch(sendMessageLogSubmission({
        data: {
          message: getMessageByVurablizeEvent(VurbalizeEvents.MEETING_CONNECT_REQUEST),
          event_name: VurbalizeEvents.MEETING_CONNECT_REQUEST,
        }
      }));
    }
    if (e.intent === IntentType.INPUT_COLLECTION) {
      dispatch(setHubSpotMeetingScheduled(false));
      dispatch(sendMessageLogSubmission({
        data: {
          turn_id: agentTurnId,
          event_name: VurbalizeEvents.INPUT_COLLECTION_TRIGGERED,
          event_data: {
            timestamp: Date.now(),
            inputType: 'email'
          }
        }
      }));
    }
    if (e.intent === IntentType.USER_FEEDBACK) {
      dispatch(setGetFeedback(true))

      dispatch(sendMessageLogSubmission({
        data: {
          event_name: VurbalizeEvents.FEEDBACK_FORM,
          message: getMessageByVurablizeEvent(VurbalizeEvents.FEEDBACK_FORM),
        }
      }));
    }
  }, [timeStart, agentTurnId, isFirstResponse, storeChat.isChattingWithLiveAgent]);

  const handleStopWaitingLA =() => {
    reCalculateOffset();
    dispatch(stopWaitingLA());
    toggleInit(true);
  }

  const handleRefreshChat = useCallback(() => {
    dispatch(getChatMessages());
    toggleInit(true);
  }, []);

  const handleServerNewResponseComplete = useCallback(({ turn_id,is_nudge }: { turn_id: string , is_nudge:boolean }) => {
    setWelcomeMessage({ ...welcomeMessage, isReceived: true });
    playAudio();
    reCalculateOffset();
    setIsFirstResponse(true);
    dispatch(setIsNudgeOpen(true));
    dispatch(handleNewResponseComplete({ turnId: turn_id }));
    dispatch(sendMessageLogSubmission({ data: {
        message: agentMessageAccumulatorRef.current,
        turn_id,
        event_name: VurbalizeEvents.USER_RECEIVED_MESSAGE,
        event_data: {
          timestamp: Date.now(),
          messageContent: agentMessageAccumulatorRef.current,
          sender: "AI Agent"
        },
        html: '',
        title_content: '',
        iframeId: '',
        iframeMode: '',
        html_url: '',
        suggestion_chips_btn: storeChat.suggestionChips
      } }));
    // Reset accumulator for next message
    agentMessageAccumulatorRef.current = '';
    toggleInit(true);
  }, [agentTurnId, storeChat.suggestionChips, storeChat.buffer, storeChat.isChattingWithLiveAgent, welcomeMessage]);

  const handleServerEventsFromBackend = useCallback((data: any) => {
    dispatch(sendMessageLogSubmission({ data }));
  }, []);

  const handleServerError = useCallback((data: any) => {
    dispatch(sendMessageLogSubmission({ data:{
      message: getMessageByVurablizeEvent(VurbalizeEvents.SOCKET_ERROR),
      event_name: VurbalizeEvents.SOCKET_ERROR,
      error_code: "2001"
    } }));
  }, []);

  const handleServerReconnectFailed = useCallback((data: any) => {
    dispatch(sendMessageLogSubmission({ data: {
      message: getMessageByVurablizeEvent(VurbalizeEvents.SOCKET_RESPONSE_TIMEOUT),
      event_name: VurbalizeEvents.SOCKET_RESPONSE_TIMEOUT,
      error_code: "2001"
    } }));
  }, [])

  const handleChatCloudResponseComplete = useCallback(({ turn_id }: {turn_id: string}) => {
    dispatch(handleNewResponseComplete({ turnId: turn_id }));
  }, [])

  const handleOperator = useCallback(() => {
    toggleInit(true);
    dispatch(setIsStreamingHandling(false));
  }, [toggleInit]) 

  const handleLiveAgentConnected = useCallback(() => {
    setOffset(0);
    dispatch(stopWaitingLA());
    dispatch(setIsChattingWithLiveAgent(true));
  }, [])

  const handleLiveAgentDisconnected = useCallback(() => {
    dispatch(setIsChattingWithLiveAgent(false));
    dispatch(setIsWaitingLiveAgent(false));
  }, [])

  const handleLiveAgentResponse = useCallback((e: LiveAgentResponse) => {
    dispatch(getChatMessages());
    dispatch(addMessage({
      itemType: "live_agent",
      value: e.system_text_response,
      avatar_link: e.avatar_link,
      turn_id: e.turn_id,
      conversation_id: storeAppState.conversationId,
    }));
  }, [storeAppState.conversationId])

  const handleOpenCalendar = useCallback(async (e : OfferCalendarResponse) => {
    if (e.calendar_type === 'custom'){
       const slots = await fetchMeetingDetails(e.calendar);
            dispatch(setMeetingDetails(slots.meeting_details));
            dispatch(setAgentDetails(slots.agent_details));
            dispatch(setAvailableSlots(slots.available_slots));
      dispatch(setBookAppointment({ implementation: BookAppointmentImplementation.CUSTOM_CALENDAR, agentEmail: e.calendar }));
    }
    else{
      dispatch(setBookAppointment({ implementation: BookAppointmentImplementation.CALENDLY  ,calendlyLink : e.calendar}));
    }
  }, [])

  const handleCallRejected = useCallback(() => {
    dispatch(endCall());
  }, [])

  
  const handleLiveAgentBrowsingMessage = useCallback(() => {
    toggleInit(true); 
    dispatch(setIsPingedByLA(true));
    dispatch(setIsLANudge(true));
    dispatch(getChatMessages());
    dispatch(setIsChattingWithLiveAgent(true));
    scrollContainerToBottom();
    dispatch(setIsNudgeOpen(true));
    dispatch(sendMessageLogSubmission({ data : {
      event_name: VurbalizeEvents.LIVE_AGENT_PINGED_USER,
      message: getMessageByVurablizeEvent(VurbalizeEvents.LIVE_AGENT_PINGED_USER),
      event_data: {
        timestamp: Date.now(),
      },
    }}))
  }, [])

  const handleShowTypingAnimation = useCallback((is_typing: boolean) => {
    setIsAgentTyping(is_typing);
    if(isAgentTyping){
      useTypingAnimation({text: 'Agent is typing...'})
    }
  }, [])

  useEffect(() => {
    // reset type for nudge on close nudge
    if (!storeAppState.isNudgeOpen) {
      dispatch(setIsLANudge(false))
    }
  }, [storeAppState.isNudgeOpen])

  useEffect(() => {
    const { current } = socket;
    if (!current) {
      return;
    }

    current.on(SocketMessageType.NEW_RESPONSE, handleServerNewResponse);
    current.on(SocketMessageType.RESPONSE_COMPLETE, handleServerNewResponseComplete);
    current.on(SocketMessageType.REFRESH_CHAT, handleRefreshChat);
    current.on(SocketMessageType.CHAT_CLOUD_RESPONSE_COMPLETE, handleChatCloudResponseComplete);  // NOTE: had merge conflicts on dev-wpa <- feature/la-browsing-msg, remove if causes error

    current.on(SocketMessageType.EVENTS_FROM_BACKEND, handleServerEventsFromBackend);
    current.on(SocketMessageType.ERROR, handleServerError);
    current.on(SocketMessageType.RECONNECT_FAILED, handleServerReconnectFailed);
    current.on(SocketMessageType.RECONNECT_FAILED, handleServerReconnectFailed);
    // force to stop waiting for response_complete
    // current.on(SocketMessageType.RESPONSE_COMPLETE, handleServerNewResponseComplete);
    
    // live agent
    current.on(SocketMessageType.OPERATOR, handleOperator);
    current.on(SocketMessageType.LIVE_AGENT_CONNECTED, (data: { agent_name: string , agent_photo : string }) => {
      handleLiveAgentConnected();
      dispatch(setAgentName(data.agent_name));
      dispatch(setAgentPhotoURL(data.agent_photo));
    });
    current.on(SocketMessageType.LIVE_AGENT_DISCONNECTED, handleLiveAgentDisconnected);
    current.on(SocketMessageType.LIVE_AGENT_RESPONSE, handleLiveAgentResponse);
    current.on(SocketMessageType.LIVE_AGENT_BROWSING_MESSAGE, handleLiveAgentBrowsingMessage);
    current.on(SocketMessageType.OFFER_CALENDAR, handleOpenCalendar);
    current.on(SocketMessageType.CALL_REJECTED, handleCallRejected);
    current.on(SocketMessageType.CHAT_ENDED, handleStopWaitingLA);
    current.on(SocketMessageType.NOTIFY_USER_OF_AGENT_TYPING, handleShowTypingAnimation);

    return () => {
      current.off(SocketMessageType.NEW_RESPONSE, handleServerNewResponse);
      current.off(SocketMessageType.RESPONSE_COMPLETE, handleServerNewResponseComplete);
      current.off(SocketMessageType.REFRESH_CHAT, handleRefreshChat);
      current.off(SocketMessageType.EVENTS_FROM_BACKEND, handleServerEventsFromBackend);
      current.off(SocketMessageType.ERROR, handleServerError);
      current.off(SocketMessageType.RECONNECT_FAILED, handleServerReconnectFailed);
      current.off(SocketMessageType.CHAT_CLOUD_RESPONSE_COMPLETE, handleChatCloudResponseComplete);
      current.off(SocketMessageType.OPERATOR, handleOperator);
      current.off(SocketMessageType.LIVE_AGENT_CONNECTED, handleLiveAgentConnected);
      current.off(SocketMessageType.LIVE_AGENT_DISCONNECTED, handleLiveAgentDisconnected);
      current.off(SocketMessageType.OFFER_CALENDAR, handleOpenCalendar);
      current.off(SocketMessageType.LIVE_AGENT_RESPONSE, handleLiveAgentResponse);
      current.off(SocketMessageType.CALL_REJECTED, handleCallRejected);
      current.off(SocketMessageType.CHAT_ENDED, handleStopWaitingLA);
      current.off(SocketMessageType.LIVE_AGENT_BROWSING_MESSAGE, handleLiveAgentBrowsingMessage);
      current.off(SocketMessageType.NOTIFY_USER_OF_AGENT_TYPING, handleShowTypingAnimation);
    };
  }, [
    handleServerNewResponse,
    handleServerNewResponseComplete,
    handleServerEventsFromBackend,
    handleServerError,
    handleServerReconnectFailed,
    handleLiveAgentResponse,
    handleLiveAgentDisconnected,
    handleLiveAgentConnected,
    handleOperator,
    handleOpenCalendar,
    handleCallRejected,
    handleLiveAgentBrowsingMessage,
    handleShowTypingAnimation,
  ]);

  const lastMessage = useMemo(() => {
    if (!storeChat.messages.length) {
      return null;
    }

    const  messages = {
      typeA: [] as ChatMessage[],
      typeU: [] as ChatMessage[],
      typeAgent: [] as ChatMessage[],
    }
    storeChat.messages.forEach((message) => {
      if (message.itemType === 'A') {
        messages.typeA.push(message);
      }
      if (message.itemType === 'U') {
        messages.typeU.push(message);
      }
      if (message.itemType === 'live_agent') {
        messages.typeAgent.push(message);
      }
    })

    return {
      typeA: messages.typeA[messages.typeA.length - 1],
      typeU: messages.typeU[messages.typeU.length - 1],
      typeAgent: messages.typeAgent[messages.typeAgent.length - 1],
    };
  }, [storeChat.messages]);


  useEffect(() => {
    if (!welcomeMessage.isSent || !welcomeMessage.isReceived) return;

    setWelcomeMessage({isSent: false, isReceived: false});

    dispatch(sendMessageLogSubmission({ data : {
      message: agentMessageAccumulatorRef.current,
      turn_id: agentTurnId,
      event_name: VurbalizeEvents.WELCOME_MESSAGE_TEXT,
      event_data: {
        timestamp: Date.now(),
        messageContent: agentMessageAccumulatorRef.current,
        searchTerm: getQueryParamValue('utm_term')
      },
      html: '',
      title_content: '',
      iframeId: '',
      iframeMode: '',
      html_url: '',
      suggestion_chips_btn: storeChat.suggestionChips,
    }}))
    // Reset accumulator for next message
    agentMessageAccumulatorRef.current = '';
  }, [welcomeMessage])

  const reCalculateOffset = (isFirstResponse = false) => {
    const gap = 10;
    const containerHeight = messagesContainerRef?.current?.offsetHeight || 400;
    const lastResponse = document.querySelector('#response-last-message') as HTMLElement;
    const lastResponseHeight = lastResponse?.offsetHeight || 0;
    const lastUser = document.querySelector('#user-last-message') as HTMLElement;
    const lastUserHeight = lastUser?.offsetHeight || 0;
    const chatControls = document.querySelector('#chat-controls') as HTMLElement;
    const chatControlsHeight = chatControls?.offsetHeight || 0;

    const verticalPadding = 15
    let heightMessages = lastUserHeight + verticalPadding * 2 + gap * 2;

    if (!isFirstResponse) {
      heightMessages += lastResponseHeight + chatControlsHeight;
    }

    const offset = containerHeight - heightMessages;

    setOffset(offset);
  }

  const setOffset = (value: number) => {
    const result = value > 0 ? value : 0;

    if (offsetRef.current) {
      offsetRef.current.style.paddingTop = `${result}px`;
    }
  }

  useEffect(() => {
    if (storeChat.isChattingWithLiveAgent) {
      setOffset(0);
      return;
    }
  }, [storeChat.isChattingWithLiveAgent])

  useEffect(() => {
    if (!lastMessage?.typeU || storeChat.isMessagesLoading) return;

    scrollToLastMessage({ block: 'nearest'});
    // scrollToLastMessage({ block: 'start' });

  }, [lastMessage?.typeU?.turn_id])

  useEffect(() => {
    if (!lastMessage?.typeAgent || storeChat.isMessagesLoading) return;

    scrollContainerToBottom();
  }, [lastMessage?.typeAgent?.turn_id])

  const scrollContainerToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }

  const providerValue = useMemo<IChatContext>(() => ({
    socket,
    sendUserInput,
    sendSuggestionChipInput,
    messagesContainerRef,
    lastMessage,
    agentTurnId,
    sendMessageToServer,
    scroll,
    offsetRef,
    handleStopWaitingLA,
    userTyping,
    forceStopUserTyping,
    markAllMessagesAsRead,
    isAgentTyping,
    setIsAgentTyping,
  }), [lastMessage, agentTurnId, userTyping, forceStopUserTyping, markAllMessagesAsRead,isAgentTyping]);

  return (
    <ChatContext.Provider
      value={providerValue}
    >
      {children}
    </ChatContext.Provider>
  );
};
